import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PencilSimpleLine, X } from "@phosphor-icons/react";

const C = { panel: "#0A2826", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B" };

const FUNDER_LABEL = { debt: "Debt", equity: "Equity", both: "Blended" };

export const IndustryBadge = ({ deck, meta, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(deck.industry || "other");
  const [otherDesc, setOtherDesc] = useState(deck.industry_other_desc || "");
  const [busy, setBusy] = useState(false);

  const templates = meta?.industry_templates || {};
  const industries = meta?.industries || [];
  const tplName = templates[deck.industry]?.name || "Other";
  const funderLabel = FUNDER_LABEL[deck.funder_type]
    || (meta?.funder_types || []).find((f) => f.id === deck.funder_type)?.name
    || deck.funder_type;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/pitch/decks/${deck.deck_id}/industry`, {
        industry: sel, industry_other_desc: sel === "other" ? otherDesc : "",
      });
      onChanged?.(data);
      setOpen(false);
    } catch {
      toast.error("Could not update industry");
    }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => { setSel(deck.industry || "other"); setOtherDesc(deck.industry_other_desc || ""); setOpen(true); }}
        data-testid="industry-badge"
        title="Change your industry template"
        className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors hover:brightness-110"
        style={{ backgroundColor: "rgba(10,139,133,0.16)", color: C.tealSoft, border: "1px solid rgba(10,139,133,0.4)" }}>
        {tplName} Template · {funderLabel} calibration
        <PencilSimpleLine size={11} weight="bold" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[90] grid place-items-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
          onClick={() => setOpen(false)} data-testid="industry-settings-modal">
          <div className="w-full max-w-2xl rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
            style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.orange }}>Deck settings</div>
                <div className="font-extrabold text-xl text-white mt-1">Change industry template</div>
                <div className="text-xs text-white/50 mt-1">
                  Changing the industry re-applies the new template and flags any slides with new mandatory content requirements.
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="industry-settings-close" className="text-white/50 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {industries.map((ind) => {
                const selected = sel === ind.id;
                return (
                  <button key={ind.id} onClick={() => setSel(ind.id)} data-testid={`industry-option-${ind.id}`}
                    className="text-left px-3 py-2.5 rounded-lg border transition-all"
                    style={{
                      backgroundColor: selected ? "rgba(10,139,133,0.14)" : "rgba(255,255,255,0.03)",
                      borderColor: selected ? C.teal : "rgba(255,255,255,0.1)",
                    }}>
                    <div className="text-[12.5px] font-bold" style={{ color: selected ? C.tealSoft : "#fff" }}>{ind.name}</div>
                  </button>
                );
              })}
            </div>

            {sel === "other" && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1.5">Briefly describe your industry</div>
                <input type="text" value={otherDesc} onChange={(e) => setOtherDesc(e.target.value)}
                  data-testid="industry-other-desc-input" placeholder="e.g. Waste recycling marketplace"
                  className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
                <div className="text-[11px] text-white/45 mt-1.5">{meta?.generic_template_note}</div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setOpen(false)} className="text-sm text-white/60 hover:text-white">Cancel</button>
              <button onClick={save} disabled={busy || sel === deck.industry && (sel !== "other" || otherDesc === (deck.industry_other_desc || ""))}
                data-testid="industry-settings-save"
                className="px-5 py-2.5 rounded-md text-sm font-bold disabled:opacity-40"
                style={{ backgroundColor: C.teal, color: "#fff" }}>
                {busy ? "Applying…" : "Apply template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IndustryBadge;
